import BarChart from "@mui/icons-material/BarChart";
import InfoOutline from "@mui/icons-material/InfoOutline";
import LinkIcon from "@mui/icons-material/Link";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState, type ChangeEvent, type FC } from "react";
import type { ErrorProps } from "../../../utils/errors/extract-zod-tree-error-props";
import type { MomentWhenClassification } from "../../moment-card/components/moment-card-when";
import type { FollowUp } from "../server/follow-up-service";
import type { FollowUpMomentDetails } from "../server/types";
import {
  ANY_LINK_DYNAMIC_TOOLTIP,
  DynamicContentChip,
  SPECIFIC_LINK_DYNAMIC_TOOLTIP,
  SURVEY_DYNAMIC_TOOLTIP,
} from "./dynamic-content-chip";
import { FollowUpLinkSelectDialog } from "./follow-up-link-select-dialog";

export const FollowUpSidebarReason: FC<{
  value: FollowUp["config"];
  onChange(reason: FollowUp["config"]): void;
  source_id: string;
  surveys: FollowUpMomentDetails["surveys"];
  error: ErrorProps;
  disabled: boolean;
  whenClassification: MomentWhenClassification;
  hyperlinks?: string[];
  showLinkClick?: boolean;
  dynamicOnlyLinks?: Set<string>;
  dynamicOnlySurveyIds?: Set<string>;
  parentStatus?: string;
}> = ({
  value,
  onChange,
  source_id,
  surveys,
  error,
  disabled,
  whenClassification,
  hyperlinks = [],
  showLinkClick = false,
  dynamicOnlyLinks = new Set(),
  dynamicOnlySurveyIds = new Set(),
  parentStatus,
}) => {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const handleChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, val: string) => {
      // Parse string back into the expected config object
      if (val === "no_interaction") {
        onChange({ type: "no_interaction", source_id });
      } else if (val.startsWith("no_survey_completion:")) {
        const surveyId = val.split(":")[1];
        if (!surveyId) return;
        onChange({ type: "no_survey_completion", survey_id: surveyId, source_id });
      } else if (val === "link_interaction") {
        onChange({ type: "link_click", source_id, hyperlink_url: null });
      }
    },
    [onChange, source_id],
  );

  const actualValue = useMemo(() => {
    if (!value) return "";

    if (value.type === "no_interaction") return "no_interaction";
    if (value.type === "no_survey_completion") return `no_survey_completion:${value.survey_id}`;
    if (value.type === "link_click" || value.type === "no_link_click") return "link_interaction";

    return "";
  }, [value]);

  const filteredHyperlinks = useMemo(
    () => hyperlinks.filter((url) => !url.includes("/survey/jwt/")),
    [hyperlinks],
  );

  const linkInteractionDisabled =
    showLinkClick && filteredHyperlinks.length === 0 && !!parentStatus && parentStatus !== "draft";

  const isLinkInteractionSelected = value?.type === "link_click" || value?.type === "no_link_click";

  const currentInteractionType = value?.type === "no_link_click" ? "not_clicked" : "clicked";

  const currentHyperlinkUrl = isLinkInteractionSelected
    ? (value as { hyperlink_url: string | null }).hyperlink_url
    : null;

  const handleInteractionTypeChange = useCallback(
    (interactionVal: string) => {
      if (!isLinkInteractionSelected) return;
      const hyperlink_url = currentHyperlinkUrl;
      if (interactionVal === "clicked") {
        onChange({ type: "link_click", source_id, hyperlink_url });
      } else {
        onChange({ type: "no_link_click", source_id, hyperlink_url });
      }
    },
    [isLinkInteractionSelected, currentHyperlinkUrl, onChange, source_id],
  );

  const handleLinkSelect = useCallback(
    (url: string | null) => {
      if (!isLinkInteractionSelected) return;
      setLinkDialogOpen(false);
      if (value?.type === "no_link_click") {
        onChange({ type: "no_link_click", source_id, hyperlink_url: url });
      } else {
        onChange({ type: "link_click", source_id, hyperlink_url: url });
      }
    },
    [isLinkInteractionSelected, value, onChange, source_id],
  );

  return (
    <FormControl error={error?.error} disabled={disabled}>
      <Stack direction="column" spacing={1}>
        <Typography id="follow-up-reason-label" variant="subtitle2">
          Select a Follow-Up reason
        </Typography>

        <RadioGroup
          aria-labelledby="follow-up-reason-label"
          name="follow-up-reason-group"
          onChange={handleChange}
          value={actualValue}
        >
          <Stack direction="column" spacing={2} alignItems="center">
            <Paper elevation={5} sx={{ p: 1, width: "100%" }}>
              <FormControlLabel
                value="no_interaction"
                control={<Radio size="small" />}
                label={
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography variant="body2">
                      {whenClassification.type === "triggered"
                        ? "Most recent Communication not seen"
                        : "Communication not seen"}
                    </Typography>
                    <Tooltip
                      title={
                        '"Seen" means clicking a link within a communication, opening an email communication, or reacting to a Slack or Microsoft Teams message with an emoji.'
                      }
                    >
                      <InfoOutline fontSize="small" color="action" />
                    </Tooltip>
                  </Stack>
                }
              />
            </Paper>

            {showLinkClick && (
              <Tooltip
                title={
                  linkInteractionDisabled ? "No trackable links found in this communication" : ""
                }
                placement="top"
              >
                <Paper elevation={5} sx={{ width: "100%", overflow: "hidden" }}>
                  <Stack direction="column" spacing={0} sx={{ minWidth: 0 }}>
                    <FormControlLabel
                      value="link_interaction"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">Link/button interaction</Typography>}
                      sx={{ p: 1 }}
                      disabled={disabled || linkInteractionDisabled}
                    />

                    {isLinkInteractionSelected && (
                      <>
                        <Divider />
                        <Stack direction="column" spacing={2} sx={{ p: 2, width: "100%" }}>
                          <Stack direction="column" spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                              Interaction
                            </Typography>
                            <Select
                              size="small"
                              value={currentInteractionType}
                              onChange={(e) => handleInteractionTypeChange(e.target.value)}
                              disabled={disabled}
                            >
                              <MenuItem value="clicked">Clicked</MenuItem>
                              <MenuItem value="not_clicked">Not clicked</MenuItem>
                            </Select>
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ width: "100%", minWidth: 0 }}
                          >
                            {currentHyperlinkUrl === null ? (
                              <>
                                <LinkIcon fontSize="small" />
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  Any link in this Communication
                                </Typography>
                              </>
                            ) : (
                              <Box sx={{ flex: 1, width: 0, overflow: "hidden" }}>
                                <Tooltip title={currentHyperlinkUrl} placement="top-start">
                                  <Link
                                    href={currentHyperlinkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {currentHyperlinkUrl}
                                  </Link>
                                </Tooltip>
                              </Box>
                            )}
                            <Button
                              data-analytics-id="follow-up-link-select-edit-button"
                              size="small"
                              variant="text"
                              onClick={() => setLinkDialogOpen(true)}
                              disabled={disabled}
                            >
                              {currentHyperlinkUrl === null ? "Select link/button" : "Edit"}
                            </Button>
                          </Stack>
                          {currentHyperlinkUrl === null && dynamicOnlyLinks.size > 0 && (
                            <DynamicContentChip tooltipText={ANY_LINK_DYNAMIC_TOOLTIP} />
                          )}
                          {currentHyperlinkUrl !== null &&
                            dynamicOnlyLinks.has(currentHyperlinkUrl) && (
                              <DynamicContentChip tooltipText={SPECIFIC_LINK_DYNAMIC_TOOLTIP} />
                            )}
                        </Stack>
                      </>
                    )}
                  </Stack>
                </Paper>
              </Tooltip>
            )}

            {(surveys || []).map((survey) => (
              <Paper key={survey.id} elevation={5} sx={{ width: "100%" }}>
                <Stack direction="column" spacing={0}>
                  <FormControlLabel
                    value={`no_survey_completion:${survey.id}`}
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        {whenClassification.type === "triggered"
                          ? "Most recent Survey incomplete"
                          : "Survey incomplete"}
                      </Typography>
                    }
                    sx={{ p: 2 }}
                  />
                  <Divider />
                  <Stack direction="column" spacing={1} sx={{ p: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <BarChart fontSize="small" />
                      <Typography variant="subtitle2">{survey.title}</Typography>
                    </Stack>
                    {dynamicOnlySurveyIds.has(survey.id) && (
                      <DynamicContentChip tooltipText={SURVEY_DYNAMIC_TOOLTIP} />
                    )}
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </RadioGroup>
        {error.helperText && <FormHelperText>{error.helperText}</FormHelperText>}
      </Stack>

      <FollowUpLinkSelectDialog
        open={linkDialogOpen}
        hyperlinks={filteredHyperlinks}
        value={currentHyperlinkUrl}
        onSelect={handleLinkSelect}
        onCancel={() => setLinkDialogOpen(false)}
        dynamicOnlyLinks={dynamicOnlyLinks}
      />
    </FormControl>
  );
};
